import { Agenda, AgendaProps, EventCard, Tag } from "@kalyzee/escaet-ui";
import { GenericPanelWithDropdown, InfoRow, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { MeetingOutputDTO, RoomOutputDTO, TimeEventOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { isArray } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import styles from "./TimeEventsAgenda.module.css";

export interface TimeEventsAgendaProps {
  timeEvents?: TimeEventOutputDTO | TimeEventOutputDTO[];
  selectedEventId?: string;
  kind?: AgendaProps["kind"];
  onSelectEvent?: (eventId: string) => void;
  onJoinEvent?: (eventId: string) => void;
}

interface EventData {
  id: string;
  title: string;
  roomName?: string;
  startDatetime: Date;
  endDatetime: Date;
  eventType: string;
  expertName: string;
  promotion?: string;
}

type EventStatus = "incoming" | "ongoing" | "finished";

const getEventStatus = (event: EventData): EventStatus => {
  const now = new Date();
  if (event.startDatetime > now) return "incoming";
  if (event.endDatetime < now) return "finished";
  return "ongoing";
}

export const convertTimeEvent = (
  event: TimeEventOutputDTO,
): EventData | undefined => {
  const meeting = event.target as MeetingOutputDTO;
  if (!meeting) return undefined;
  const expert = meeting.actors?.find((a) => a.externalProvider === "escaet:expert");
  const room = event.room as RoomOutputDTO; // populated room
  return {
    id: event._id,
    title: meeting.name || "",
    roomName: room?.name,
    startDatetime: new Date(event.startDatetime),
    endDatetime: new Date(event.endDatetime),
    eventType: "cours",
    expertName: expert?.identity?.username ?? expert?.user?.identity?.username ?? "",
    promotion: (meeting as any).promos?.join(' '),
  };
}

const SortMethods: Record<string, string> = {
  date: "Date",
  name: "Name",
  room: "Room",
};

const SortValues = Object.keys(SortMethods);

const EventItem = ({ data, onJoin }: { data: EventData, onJoin: () => void }) => {
  const [status, setStatus] = useState<EventStatus>(() => getEventStatus(data));

  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(getEventStatus(data));
    }, 5000);
    return () => clearInterval(interval);
  }, [data]);

  const renderStatus = () => (
    <p style={{ color: status === "ongoing" ? 'var(--color-element-calendar-item-status-ongoing)' : 'var(--color-element-calendar-item-status)' }}>
      Évènement {status === "incoming" ? "à venir" : status === "ongoing" ? "en cours" : "terminé"}
    </p>
  );

  return (
    <InfoRow
      date={data.startDatetime}
      header={data.roomName}
      title={data.title}
      leftIcon={<ReactIcons.Clock2 width={12} color={"var(--color-agenda-title)"} />}
      rightContent={renderStatus()}
      customButton={
        status === "ongoing" && ( 
          <PrimaryButton
            text="Rejoindre"
            onClick={onJoin}
            // TODO : forced to use inline cause there's some style override in the lib, 
            className={styles.joinBtn}
          />
        )
      }
    />
  )
}; 


export const TimeEventsAgenda = ({ timeEvents, selectedEventId, kind, onSelectEvent, onJoinEvent }: TimeEventsAgendaProps) => {
  const [sortMethod, setSortMethod] = useState<string>(SortValues[0]);

  const getDropdownLabel = useCallback((value: string) => {
    // Todo : translate
    return SortMethods[value] || "Unknown";
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSortMethod(value);
  }, []);

  const eventsData = useMemo<EventData[]>(() => {
    if (!timeEvents) return [];
    const _timeEvents = isArray(timeEvents) ? timeEvents : [timeEvents];
    const eventsData: EventData[] = [];
    _timeEvents.forEach((timeEvent) => {
      const eventData = convertTimeEvent(timeEvent);
      if (eventData) eventsData.push(eventData);
    });
    return eventsData;
  }, [timeEvents]);

  const sortedEventsData = useMemo<EventData[]>(() => {
    return [...eventsData].sort((a, b) => {
      switch (sortMethod) {
        case "date":
          return a.startDatetime.getTime() - b.startDatetime.getTime();
        case "name":
          return a.title.localeCompare(b.title);
        case "room":
          return (a.roomName || "").localeCompare(b.roomName || "");
        default:
          return 0;
      }
    });
  }, [eventsData, sortMethod]);

  const eventNodes = useMemo(() => {
    return sortedEventsData.map((event) => (
      <EventItem 
        key={event.id} 
        data={event} 
        onJoin={() => onJoinEvent?.(event.id)}
      />
    ));
  }, [sortedEventsData]);
  
  if (!isArray(timeEvents)) {
    const data = eventsData[0];
    if (!data) {
      return (
        <div style={{ width: '100%', borderRadius: '15px', height: '200px', backgroundColor: 'rgb(102 102 102)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'lightgrey'}}>
          L'event ne semble pas exister
        </div>
      );
    }
  }
    
  return (
    <GenericPanelWithDropdown
      style={{ 
        height: "100%",
        boxShadow: "var(--shadow-element-box)",
      }}
      items={eventNodes}
      mainTitle="AGENDA"
      subtitle="Activités"
      dropdownOptions={eventNodes.length > 1 ? SortValues : undefined}
      getDropdownLabel={getDropdownLabel}
      onSelectOption={handleSortChange}
    />
  );
};
